import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { isPlatformAdminUser } from 'app/core/auth/platform-admin.util';
import { UserService } from 'app/core/user/user.service';
import { ProfileService } from 'app/core/profile/profile.service';
import { User } from 'app/core/user/user.types';
import { BehaviorSubject, catchError, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { environment } from 'environments/environment';
import { Router } from '@angular/router';

const USER_ROLES_KEY = 'user-roles';
const USER_PERMISSIONS_KEY = 'user-permissions';
const ACTIVE_PROFILE_KEY = 'active-profile-id';
const USER_DATA_KEY = 'user-data';

export interface VerifyResponse {
  userUID: string;
  profileUID: string;
  accountUID: string;
  subAccountUID: string;
  displayName: string;
  permissionsByProduct: {
    AIVA: string[];
  };
  roles: Array<{
    displayName: string;
    name: string;
    key: string;
    product: {
      uid: string;
      displayName: string;
      key: string;
    };
  }>;
  account: { uid: string };
  subaccount: { uid: string };
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly _httpClient = inject(HttpClient);
  private readonly _userService = inject(UserService);
  private readonly _profileService = inject(ProfileService);
  private readonly _router = inject(Router);

  private _authenticated: boolean = false;
  private _verifyResponse = new BehaviorSubject<VerifyResponse | null>(null);

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Setter & getter for access token
   */
  set accessToken(token: string) {
    localStorage.setItem('accessToken', token);
  }

  set refreshToken(token: string) {
    localStorage.setItem('refreshToken', token);
  }

  get accessToken(): string {
    return localStorage.getItem('accessToken') ?? '';
  }

  get refreshToken(): string {
    return localStorage.getItem('refreshToken') ?? '';
  }

  get verifyResponse$(): Observable<VerifyResponse | null> {
    return this._verifyResponse.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Forgot password
   *
   * @param email
   */
  forgotPassword(email: string): Observable<unknown> {
    return this._httpClient.post(`${environment.authenticationApiUrl}/reset-password`, {
      identifier: email,
      channel: 'EMAIL',
    });
  }

  /**
   * Validate the OTP of a reset password challenge
   *
   * @param challenge
   * @param otp
   */
  validateResetPasswordOtp(challenge: string, otp: string): Observable<unknown> {
    return this._httpClient.get(
      `${environment.authenticationApiUrl}/reset-password/challenges/${encodeURIComponent(challenge)}`,
      { params: { otp } }
    );
  }

  /**
   * Reset password
   *
   * @param challenge
   * @param otp
   * @param newPassword
   */
  resetPassword(challenge: string, otp: string, newPassword: string): Observable<unknown> {
    return this._httpClient.post(
      `${environment.authenticationApiUrl}/reset-password/challenges/${encodeURIComponent(challenge)}`,
      { otp, newPassword }
    );
  }

  /**
   * Change Password
   *
   * @param oldPassword
   * @param newPassword
   */
  changePassword(oldPassword: string, newPassword: string): Observable<unknown> {
    return this._httpClient.patch(`${environment.authenticationApiUrl}/reset-password/authenticated/change`, {
      oldPassword,
      newPassword,
    });
  }

  /**
   * Sign in
   *
   * @param credentials
   */
  signIn(credentials: { email: string; password: string }): Observable<unknown> {
    return this._httpClient.post(`${environment.authenticationApiUrl}/auth/login`, credentials).pipe(
      switchMap((response: unknown) => {
        const authResponse = response as { accessToken: string; refreshToken: string; user: User };
        this.accessToken = authResponse.accessToken;
        this.refreshToken = authResponse.refreshToken;

        const user: User = {
          ...authResponse.user,
          email: authResponse.user?.email || credentials.email,
        };
        this._userService.user = user;
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(user));

        return this.verify().pipe(
          tap(() => {
            this._authenticated = true;
          }),
          switchMap(() => {
            return of(response);
          })
        );
      })
    );
  }

  getRoles(): string[] {
    const roles = localStorage.getItem(USER_ROLES_KEY);
    return roles ? JSON.parse(roles) : [];
  }

  hasRole(role: string): boolean {
    const roles = this.getRoles();
    return roles.includes(role);
  }

  getPermissions(): string[] {
    const permissions = localStorage.getItem(USER_PERMISSIONS_KEY);
    return permissions ? JSON.parse(permissions) : [];
  }

  hasPermission(permission: string): boolean {
    const permissions = this.getPermissions();
    return permissions.includes(permission);
  }

  hasAnyPermission(requiredPermissions: string[]): boolean {
    const userPermissions = this.getPermissions();
    return requiredPermissions.some(permission => userPermissions.includes(permission));
  }

  hasAnyAdminPermission(): boolean {
    const permissions = this.getPermissions();
    return permissions.some(permission => permission.endsWith('_admin'));
  }

  /** Platform administrator — see isPlatformAdminUser (AIVA-2143). */
  isPlatformAdmin(): boolean {
    return isPlatformAdminUser(this.getPermissions(), this._verifyResponse.value);
  }

  /**
   * Sign in using the access token
   */
  signInUsingToken(): Observable<boolean> {
    // Sign in using the token
    return this._httpClient
      .post('api/auth/sign-in-with-token', {
        accessToken: this.accessToken,
      })
      .pipe(
        catchError(() =>
          // Return false
          of(false)
        ),
        switchMap((response: unknown) => {
          if (typeof response === 'boolean') {
            return of(response);
          }
          const authResponse = response as { accessToken?: string; user?: User };
          // Replace the access token with the new one if it's available on
          // the response object.
          //
          // This is an added optional step for better security. Once you sign
          // in using the token, you should generate a new one on the server
          // side and attach it to the response object. Then the following
          // piece of code can replace the token with the refreshed one.
          if (authResponse.accessToken) {
            this.accessToken = authResponse.accessToken;
          }

          // Set the authenticated flag to true
          this._authenticated = true;

          // Store the user on the user service
          if (authResponse.user) {
            this._userService.user = authResponse.user;
          }

          // Return true
          return of(true);
        })
      );
  }

  /**
   * Refresh the access token
   */
  refreshTokens(): Observable<string> {
    const refreshToken = this.refreshToken;
    if (!refreshToken) {
      this.signOut();
      return throwError(() => new Error('No refresh token'));
    }

    return this._httpClient
      .post<{ accessToken: string; refreshToken: string }>(`${environment.authenticationApiUrl}/auth/refresh`, {
        token: refreshToken,
      })
      .pipe(
        switchMap((response) => {
          this.accessToken = response?.accessToken;
          this.refreshToken = response?.refreshToken;
          return of(response.accessToken);
        }),
        catchError(() => {
          this.signOut();
          return throwError(() => new Error('Error refreshing token'));
        })
      );
  }

  /**
   * Sign out
   */
  signOut(): Observable<boolean> {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem(USER_ROLES_KEY);
    localStorage.removeItem(USER_PERMISSIONS_KEY);
    localStorage.removeItem(ACTIVE_PROFILE_KEY);
    localStorage.removeItem(USER_DATA_KEY);

    this._profileService.clearProfiles();

    // Set the authenticated flag to false
    this._authenticated = false;

    this._router.navigate(['/sign-in']);

    // Return the observable
    return of(true);
  }

  /**
   * Sign up
   *
   * @param user
   */
  signUp(user: { name: string; email: string; password: string; company: string }): Observable<unknown> {
    return this._httpClient.post('api/auth/sign-up', user);
  }

  /**
   * Unlock session
   *
   * @param credentials
   */
  unlockSession(credentials: { email: string; password: string }): Observable<unknown> {
    return this._httpClient.post('api/auth/unlock-session', credentials);
  }

  /**
   * Check the authentication status
   */
  check(): Observable<boolean> {
    if (this._authenticated) {
      return of(true);
    }

    const token = this.accessToken;
    if (!token) {
      return of(false);
    }

    if (AuthUtils.isTokenExpired(token)) {
      return of(false);
    }

    this._authenticated = true;
    return of(true);
  }

  /**
   * verify permissions
   */
  verify(): Observable<VerifyResponse> {
    return this._httpClient.post<VerifyResponse>(`${environment.authenticationApiUrl}/auth/verify`, {}).pipe(
      tap((response: VerifyResponse) => {
        this._verifyResponse.next(response);
        const roles = response.roles.map(role => role.name);
        localStorage.setItem(USER_ROLES_KEY, JSON.stringify(roles));
        localStorage.setItem(USER_PERMISSIONS_KEY, JSON.stringify(response.permissionsByProduct?.AIVA || []));
        if (response.profileUID) {
          localStorage.setItem(ACTIVE_PROFILE_KEY, response.profileUID);
        }

        const currentUserData = this._getUserDataFromStorage();
        const updatedUser: User = {
          id: response.userUID,
          name: response.displayName,
          email: currentUserData?.email || '',
        };
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(updatedUser));
        this._userService.user = updatedUser;
      })
    );
  }

  private _getUserDataFromStorage(): User | null {
    const userData = localStorage.getItem(USER_DATA_KEY);
    return userData ? JSON.parse(userData) : null;
  }

  restoreUserFromStorage(): void {
    const userData = this._getUserDataFromStorage();
    if (userData) {
      this._userService.user = userData;
    }
  }
}
